import { useState, useEffect } from "react";
import MemberLayout from "@/layouts/MemberLayout";
import { useSearchAgents, useConnectAgent, useDisconnectAgent, useGetMe } from "@/lib/api-client-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Briefcase, MapPin, Search, Star, ShieldCheck, Mail, Phone, FileText, BadgeCheck, Plus, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { motion, Variants } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" },
  }),
};

function AgentListItem({ a, isConnected, index, onConnect, onDisconnect, isPending, inPopup = false }: any) {
  const [showDetails, setShowDetails] = useState(true);

  return (
    <motion.div
      custom={index}
      initial="hidden"
      animate="visible"
      variants={fadeUp}
      className={`group relative bg-card rounded-2xl border ${
        isConnected
          ? 'border-primary/40 shadow-sm shadow-primary/5 ring-1 ring-primary/20'
          : 'border-border hover:border-primary/30'
      } p-5 sm:p-6 lg:p-7 space-y-4 sm:space-y-5 shadow-xs hover:shadow-md transition-all`}
    >
      {/* Top Header Row: Agent Info + Action Button */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5 sm:gap-4 min-w-0">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center text-primary border border-primary/20 shrink-0 shadow-xs">
            <ShieldCheck className="w-6 h-6 sm:w-7 sm:h-7" />
          </div>

          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className={`font-bold text-base sm:text-lg text-foreground ${!isConnected && 'group-hover:text-primary'} transition-colors truncate`}>
                {a.name}
              </h3>
              {isConnected && (
                <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-semibold flex items-center gap-1 border border-emerald-500/20 shadow-xs">
                  <Star className="w-3 h-3 fill-current" />
                  Connected
                </span>
              )}
              {a.agentType && (
                <span className="px-2.5 py-0.5 rounded-full bg-muted text-muted-foreground text-xs font-semibold border border-border/70">
                  {a.agentType}
                </span>
              )}
            </div>
            <p className="text-muted-foreground text-xs sm:text-sm flex items-center gap-1.5 font-medium">
              <Briefcase className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary shrink-0" />
              <span>{a.agencyName || "Independent Agent"}</span>
            </p>
          </div>
        </div>

        {/* Action Button */}
        <div className="shrink-0 w-full sm:w-auto">
          {isConnected ? (
            <Button
              variant="destructive"
              className="w-full sm:w-auto rounded-xl h-10 px-5 font-semibold transition-all shadow-sm"
              onClick={() => onDisconnect(a.id)}
              disabled={isPending}
            >
              Disconnect
            </Button>
          ) : (
            <Button
              variant="default"
              className="w-full sm:w-auto rounded-xl h-10 px-5 font-semibold transition-all shadow-sm shadow-primary/25 hover:shadow-md hover:shadow-primary/30"
              onClick={() => onConnect(a.id)}
              disabled={isPending}
            >
              Connect
            </Button>
          )}
        </div>
      </div>

      {inPopup && (
        <div className="pt-0.5">
          <button
            type="button"
            onClick={() => setShowDetails(!showDetails)}
            className="text-xs sm:text-sm text-primary hover:underline font-semibold inline-flex items-center gap-1 cursor-pointer"
          >
            {showDetails ? "Hide details" : "View details"}
          </button>
        </div>
      )}

      {/* Details Grid & Connected Members */}
      {showDetails && (
        <div className="space-y-4 pt-4 sm:pt-5 border-t border-border/60">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-3">
            {a.email && (
              <div className="flex items-center gap-2.5 p-2.5 sm:p-3 rounded-xl bg-muted/40 border border-border/60 hover:bg-muted/60 transition-colors">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 text-primary">
                  <Mail className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="block text-[10px] uppercase font-bold text-muted-foreground/80 tracking-wider">Email</span>
                  <span className="text-xs sm:text-sm font-semibold text-foreground truncate block" title={a.email}>{a.email}</span>
                </div>
              </div>
            )}
            {a.phone && (
              <div className="flex items-center gap-2.5 p-2.5 sm:p-3 rounded-xl bg-muted/40 border border-border/60 hover:bg-muted/60 transition-colors">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0 text-emerald-600 dark:text-emerald-400">
                  <Phone className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="block text-[10px] uppercase font-bold text-muted-foreground/80 tracking-wider">Phone</span>
                  <span className="text-xs sm:text-sm font-semibold text-foreground truncate block">{a.phone}</span>
                </div>
              </div>
            )}
            {a.npnNumber && (
              <div className="flex items-center gap-2.5 p-2.5 sm:p-3 rounded-xl bg-muted/40 border border-border/60 hover:bg-muted/60 transition-colors">
                <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0 text-blue-600 dark:text-blue-400">
                  <BadgeCheck className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="block text-[10px] uppercase font-bold text-muted-foreground/80 tracking-wider">NPN</span>
                  <span className="text-xs sm:text-sm font-semibold text-foreground truncate block">NPN: {a.npnNumber}</span>
                </div>
              </div>
            )}
            {a.licenseNumber && (
              <div className="flex items-center gap-2.5 p-2.5 sm:p-3 rounded-xl bg-muted/40 border border-border/60 hover:bg-muted/60 transition-colors">
                <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0 text-amber-600 dark:text-amber-400">
                  <FileText className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="block text-[10px] uppercase font-bold text-muted-foreground/80 tracking-wider">License</span>
                  <span className="text-xs sm:text-sm font-semibold text-foreground truncate block">Lic: {a.licenseNumber}</span>
                </div>
              </div>
            )}
            {a.experience && (
              <div className="flex items-center gap-2.5 p-2.5 sm:p-3 rounded-xl bg-muted/40 border border-border/60 hover:bg-muted/60 transition-colors">
                <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center shrink-0 text-purple-600 dark:text-purple-400">
                  <Star className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="block text-[10px] uppercase font-bold text-muted-foreground/80 tracking-wider">Experience</span>
                  <span className="text-xs sm:text-sm font-semibold text-foreground truncate block">{a.experience} yrs exp</span>
                </div>
              </div>
            )}
            {a.licensedStates && a.licensedStates.length > 0 && (
              <div className="flex items-center gap-2.5 p-2.5 sm:p-3 rounded-xl bg-muted/40 border border-border/60 hover:bg-muted/60 transition-colors">
                <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center shrink-0 text-cyan-600 dark:text-cyan-400">
                  <MapPin className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="block text-[10px] uppercase font-bold text-muted-foreground/80 tracking-wider">Jurisdiction</span>
                  <span className="text-xs sm:text-sm font-semibold text-foreground truncate block">{a.licensedStates.length} licensed state{a.licensedStates.length > 1 ? 's' : ''}</span>
                </div>
              </div>
            )}
          </div>

          {a.connectedAgents && a.connectedAgents.length > 0 && (
            <div className="p-3.5 sm:p-4 rounded-xl bg-muted/30 border border-border/60">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5 text-primary" /> Connected Team Members ({a.connectedAgents.length})
              </p>
              <div className="flex flex-wrap gap-2">
                {a.connectedAgents.map((ca: any) => (
                  <div key={ca.id || ca.email} className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-1.5 text-xs shadow-xs">
                    <span className="font-semibold text-foreground">{ca.name}</span>
                    <span className="text-muted-foreground text-[11px]">({ca.email})</span>
                    {ca.status && (
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                        ca.status === 'ACCEPTED'
                          ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20'
                          : 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'
                      }`}>
                        {ca.status}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}

export default function MemberAgents() {
  const [user, setUser] = useState<any>(null);
  useEffect(() => {
    const u = localStorage.getItem("user");
    if (u) {
      try { setUser(JSON.parse(u)); } catch (e) { }
    }
  }, []);

  const { data: meUser } = useGetMe();
  const { data: agents, isLoading: loadingAgents } = useSearchAgents();
  const connectAgent = useConnectAgent();
  const disconnectAgent = useDisconnectAgent();
  const [searchQuery, setSearchQuery] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const effectiveUser = meUser || user;
  const connectedAgentId = effectiveUser?.member?.agentId || effectiveUser?.provider?.agentId || effectiveUser?.agentId || user?.member?.agentId || user?.provider?.agentId || user?.agentId;
  let connectedAgent = agents?.find((a: any) => a.id === connectedAgentId || a.userId === connectedAgentId);

  if (!connectedAgent && (effectiveUser?.member?.agent || effectiveUser?.provider?.agent)) {
    const ag = effectiveUser?.member?.agent || effectiveUser?.provider?.agent;
    if (ag) {
      connectedAgent = {
        id: ag.id,
        userId: ag.userId,
        name: ag.user?.displayName || `${ag.user?.firstName || ''} ${ag.user?.lastName || ''}`.trim() || ag.agencyName || "Connected Agent",
        email: ag.user?.email,
        phone: ag.user?.phone,
        agencyName: ag.agencyName,
        experience: ag.experience,
        licensedStates: ag.licensedStates,
        agentType: ag.agentType,
        npnNumber: ag.npnNumber,
        licenseNumber: ag.licenseNumber,
      };
    }
  }

  const filteredAgents = agents?.filter((a: any) => {
    if (connectedAgent && (a.id === connectedAgent.id || a.userId === connectedAgent.userId)) return false;
    return a.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.agencyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.email?.toLowerCase().includes(searchQuery.toLowerCase());
  }) || [];

  const handleConnect = (agentId: string) => {
    connectAgent.mutate(agentId, {
      onSuccess: () => {
        const u = localStorage.getItem("user");
        if (u) {
          try {
            const parsed = JSON.parse(u);
            if (parsed.member) parsed.member.agentId = agentId;
            localStorage.setItem("user", JSON.stringify(parsed));
            setUser(parsed);
          } catch (e) { }
        }
        setIsPopupOpen(false);
      }
    });
  };

  const handleDisconnect = (agentId: string) => {
    disconnectAgent.mutate(agentId, {
      onSuccess: () => {
        const u = localStorage.getItem("user");
        if (u) {
          try {
            const parsed = JSON.parse(u);
            if (parsed.member) parsed.member.agentId = null;
            localStorage.setItem("user", JSON.stringify(parsed));
            setUser(parsed);
          } catch (e) { }
        }
      }
    });
  };

  return (
    <MemberLayout>
      <div className="w-full space-y-6 sm:space-y-8 pt-4 sm:pt-6 lg:pt-8 pb-16 sm:pb-20 flex flex-col min-h-[calc(100vh-10rem)] relative">
        <div className="absolute inset-0 overflow-hidden pointer-events-none -z-10">
          <div className="absolute top-0 -left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute top-40 -right-10 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        </div>

        <motion.div
          custom={0}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 sm:pb-6 border-b border-border/60"
        >
          <div className="flex items-center gap-3.5 sm:gap-4 min-w-0">
            <div className="p-2.5 sm:p-3 bg-primary/10 rounded-2xl flex-shrink-0 text-primary border border-primary/20 shadow-xs">
              <ShieldCheck className="w-6 h-6 text-primary" />
            </div>
            <div className="space-y-0.5 min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">My Agent</h1>
              <p className="text-xs sm:text-sm font-medium text-muted-foreground">
                Manage your connected licensed agent to help you navigate your care seamlessly.
              </p>
            </div>
          </div>
        </motion.div>

        <div className="space-y-4 flex-1">
          {loadingAgents ? (
            Array.from({ length: 1 }).map((_, i) => (
              <Skeleton key={i} className="w-full h-44 rounded-2xl bg-muted/60" />
            ))
          ) : connectedAgentId && connectedAgent ? (
            <AgentListItem
              a={connectedAgent}
              isConnected={true}
              index={1}
              onConnect={handleConnect}
              onDisconnect={handleDisconnect}
              isPending={disconnectAgent.isPending}
              inPopup={false}
            />
          ) : (
            <motion.div
              custom={1}
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              className="flex flex-col items-center justify-center py-14 sm:py-16 px-6 text-center bg-card rounded-2xl sm:rounded-3xl border border-dashed border-border hover:border-primary/50 transition-colors shadow-xs"
            >
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-3.5 text-primary shadow-xs">
                <ShieldCheck className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold mb-1.5 text-foreground">No Agent Connected</h3>
              <p className="text-xs sm:text-sm text-muted-foreground max-w-sm mb-5">You don't have an agent right now. Click below to add an agent.</p>
              <Button onClick={() => setIsPopupOpen(true)} className="rounded-xl h-10 sm:h-11 px-5 gap-2 font-semibold text-white shadow-md shadow-primary/20 hover:shadow-primary/30 transition-all">
                <Plus className="w-4 h-4" /> Add Agent
              </Button>
            </motion.div>
          )}
        </div>
      </div>

      <Dialog open={isPopupOpen} onOpenChange={setIsPopupOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col p-0 overflow-hidden rounded-2xl sm:rounded-3xl shadow-2xl border border-border bg-card">
          <DialogHeader className="p-5 sm:p-6 pb-4 border-b border-border bg-card/95 backdrop-blur-md">
            <DialogTitle className="text-xl sm:text-2xl font-bold text-foreground">Available Agents</DialogTitle>
            <div className="relative w-full mt-3 shadow-xs rounded-xl overflow-hidden">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-muted-foreground">
                <Search className="w-4 h-4" />
              </div>
              <Input
                placeholder="Search by name or agency..."
                className="pl-10 h-10 sm:h-11 border-border text-sm focus-visible:ring-primary rounded-xl bg-muted/40 text-foreground placeholder:text-muted-foreground"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 bg-muted/10">
            {filteredAgents.length > 0 ? (
              filteredAgents.map((a: any, index: number) => (
                <AgentListItem
                  key={a.id}
                  a={a}
                  isConnected={false}
                  index={index + 1}
                  onConnect={handleConnect}
                  onDisconnect={handleDisconnect}
                  isPending={connectAgent.isPending}
                  inPopup={true}
                />
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-12 h-12 rounded-2xl bg-muted border border-border flex items-center justify-center mb-3 text-muted-foreground">
                  <Search className="w-6 h-6" />
                </div>
                <h3 className="text-base font-semibold text-foreground">No agents found</h3>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </MemberLayout>
  );
}
